/**
 * §18.5's funded window: what the current balance bought, or nothing while
 * that is unconfirmed.
 *
 * Renders nothing while `useFundedWindow` is loading or has not landed --
 * `retry: false` there is deliberate (see that hook's module doc), and a
 * 404 here means the route does not exist yet, not that the window is
 * empty. Same silence as `AcquisitionStatus`'s unconfirmed branch and
 * `MissedSchedule`, for the same reason.
 */

import type { ReactNode } from 'react';

import { Absent } from '../../../chrome/measure';
import { PageSection, StatCard, StatGrid } from '../../../chrome/page';
import { getLocale, type Locale } from '../../../i18n';
import { credits as formatCredits, integer } from '../../../i18n/format';
import { bm } from './messages';
import { shedAccounts } from './state';
import { useFundedWindow } from './useFundedWindow';

/** The one date formatter this task's components share -- `MissedSchedule`
 *  imports it too, rather than each building its own `Intl.DateTimeFormat`. */
export function formatDate(
  iso: string,
  locale: Locale,
  options: Intl.DateTimeFormatOptions,
): string {
  return new Intl.DateTimeFormat(locale, options).format(new Date(iso));
}

export function FundedWindowCard(props: { tenantId: string }): ReactNode {
  const { data: fundedWindow } = useFundedWindow(props.tenantId);
  const locale = getLocale();

  if (fundedWindow === undefined) return null;

  return (
    <PageSection title={bm('window.title')}>
      <StatGrid>
        <StatCard
          label={bm('window.until')}
          value={
            fundedWindow.funded_until === null
              ? bm('window.none')
              : formatDate(fundedWindow.funded_until, locale, {
                  dateStyle: 'short',
                  timeStyle: 'short',
                })
          }
        />
        {/* §18.1: an unpriced operation is reported unpriced, never as a
            figure that happens to be zero. */}
        <StatCard
          label={bm('window.toHorizon')}
          value={
            fundedWindow.credits_to_fund_horizon === null ? (
              <Absent reason={{ text: bm('window.unpriced') }} />
            ) : (
              formatCredits(fundedWindow.credits_to_fund_horizon, locale)
            )
          }
        />
        <StatCard
          label={bm('window.funded')}
          value={integer(fundedWindow.funded_accounts.length, locale)}
        />
        <StatCard
          label={bm('window.shed')}
          value={integer(shedAccounts(fundedWindow).length, locale)}
        />
      </StatGrid>
    </PageSection>
  );
}
